import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { TypeOfKey } from '../../model/type-of-key';
import { KeyTypeService } from '../../service/data/key-type.service';
import { SelectionItem } from '../../../core/component/select/select.component';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { Account } from './../../../accounting/model/account';
import { Currency, CurrencySymbol, Money } from '../../../core/model/money';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { CompanyService } from '../../service/data/company.service';
import { switchMap, tap } from 'rxjs/operators';

@Component({
    selector: 'key-type-form',
    templateUrl: './key-type-form.component.html',
    styleUrls: ['./key-type-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: KeyTypeService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class KeyTypeFormComponent extends FormComponentBase<TypeOfKey> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _accountPlanService: AccountPlanService,
    private _companyService: CompanyService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    service: KeyTypeService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  currencySelection: SelectionItem[];

  accountPlans: AccountPlan[];
  incomeAccounts: { accountPlanId: string; accounts: SelectionItem[] }[];
  getIncomeAccountSelection(accountPlanId: string): SelectionItem[] {
    if (!this.incomeAccounts) return [];
    return this.incomeAccounts.find(a => a.accountPlanId == accountPlanId).accounts;
  }

  vatPercentages = [
    { name: 'ARTICLE.NO_VAT', value: 0 },
    { name: '25%', value: 25 },
    { name: '12%', value: 12 },
    { name: '6%', value: 6 },
  ];

  currencies = [{ name: CurrencySymbol(Currency.SEK), value: Currency.SEK }];

  protected createModel(): TypeOfKey {
    return new TypeOfKey();
  }

  codingForms: FormGroup[];

  protected getFormGroup(): FormGroup {
    let form = this._fb.group({
      id: this._fc.createHidden(this.dataContext ? this.dataContext.id : null),
      name: this._fc.createMandatory('KEYTYPE.NAME', this.dataContext ? this.dataContext.name : null),
      currencyCode: this._fc.create('COMMON.CURRENCY', this.dataContext.currencyCode),
      keyPrice: this._fc.createMandatory(
        'KEYTYPE.KEY_PRICE',
        this.dataContext ? this.dataContext.keyPrice : new Money(this.dataContext.currencyCode, 0)
      ),
      vatPercentage: this._fc.create('ARTICLE.VAT_PERCENT', this.dataContext ? this.dataContext.vatPercentage : 0),
      codings: this._fb.array(this.createCodingForms()),
    });
    this.AddSubscription(
      form.controls['currencyCode'].valueChanges.subscribe(val => {
        this.dataContext.currencyCode = val;
        if (form.controls['keyPrice'].value) {
          form.controls['keyPrice'].setValue(
            new Money(val, form.controls['keyPrice'].value ? form.controls['keyPrice'].value.amount : new Money(val, 0))
          );
        }
      })
    );
    return form;
  }

  ngOnInit() {
    this.AddSubscription(
      this._accountPlanService
        .getExtensiveList()
        .pipe(
          switchMap(accountPlans => {
            this.accountPlans = accountPlans;
            this.incomeAccounts = accountPlans.map(plan => {
              return { accountPlanId: plan.id, accounts: plan.incomeAccounts.map(acc => new SelectionItem(acc.displayName, acc.id)) };
            });
            super.ngOnInit();
            return this._companyService.getUsedCurrencies().pipe(
              tap(res => {
                this.currencySelection = res.map(c => new SelectionItem(CurrencySymbol(c), c));
                if (this.isNew) {
                  this.form.controls['currencyCode'].setValue(this.currencySelection[0].value);
                  this.form.controls['keyPrice'].setValue(new Money(this.currencySelection[0].value, 0));
                }
              })
            );
          })
        )
        .subscribe()
    );
  }

  createCodingForms(): FormGroup[] {
    let forms: FormGroup[] = [];
    for (let accountPlan of this.accountPlans) {
      let config = this.dataContext.codings.find(c => c.accountPlanId == accountPlan.id);
      forms.push(
        this._fb.group({
          id: this._fc.createHidden(config ? config.id : undefined),
          accountPlanId: this._fc.createHidden(accountPlan.id),
          accountPlanName: this._fc.createHidden(accountPlan.name),
          incomeAccountId: this._fc.create(
            'COMMON.INCOME_ACCOUNT',
            config ? config.incomeAccountId : null,
            null,
            null,
            'PRICELIST.HELP_TEXT.INCOME_ACCOUNT'
          ),
        })
      );
    }
    this.codingForms = forms;
    return forms;
  }
}
